import { unique } from '@front-app/util/array';
import { EMPTY_COUNTRY, EMPTY_REGION } from '@front-app/components/inputs/CountryPickerData';
import { STATUS } from '@front-app/constants/state';
import { STARTABLE_STATUSES, StoreCache, useCache } from '@front-app/store/util/cache';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import {
	UpsertProgramRequest,
	StepContentTypeEnum,
	ModuleResponse,
	PartResponse,
	UpsertStepRequest,
	UpsertModuleRequest,
	UpsertPartRequest,
	StepAction,
	FullProgramResponse,
	SimpleProgramResponse,
	CertificateLocale,
} from '@front-app/api-generated/learning-management-api';
import type {
	LearningModule,
	LearningPart,
	LearningProgram,
	LearningStep,
} from '../Model';
import {
	LmsAdminApi,
	lmsAdminApi,
	LmsUserApi,
	lmsUserApi,
} from '../api/lms-api';
import { isFullProgram } from '../util/types';
import { getMaxDateAsStringOrNull, isProgramPublished } from '../util/util';
import type { LearningProgramStore } from './model';

export const useLearningProgramsStore = (
	userApi: LmsUserApi,
	adminApi: LmsAdminApi,
	outerGroupPrograms: StoreCache<string, string[]>,
	outerPrograms: StoreCache<string, LearningProgram>,
	outerModules: StoreCache<string, LearningModule>,
	outerParts: StoreCache<string, LearningPart>,
	outerSteps: StoreCache<string, LearningStep>,
): LearningProgramStore => {
	const programs = useCache(outerPrograms, getEmptyProgram);
	const modules = useCache(outerModules, getEmptyModule);
	const parts = useCache(outerParts, getEmptyPart);
	const steps = useCache(outerSteps, getEmptyStep);
	const groupPrograms = useCache(outerGroupPrograms, () => []);

	const setPartResponse = (learningPart: PartResponse) => {
		const { steps: stepResponses, ...resultWithoutSteps } = learningPart;
		const stepIds = learningPart.steps.map(({ id }) => id);
		parts.setWithStatus(learningPart.id, { ...resultWithoutSteps, steps: stepIds }, STATUS.SUCCESS);
		stepResponses.forEach((step) => steps.setWithStatus(step.id, step, STATUS.SUCCESS));
	};

	const setModuleResponse = (learningModule: ModuleResponse) => {
		const { parts: partResponses, ...resultWithoutParts } = learningModule;
		const partIds = learningModule.parts.map(({ id }) => id);
		modules.setWithStatus(learningModule.id, { ...resultWithoutParts, parts: partIds }, STATUS.SUCCESS);
		partResponses.forEach((part) => setPartResponse(part));
	};

	const setProgramResponse = (program: FullProgramResponse | SimpleProgramResponse) => {
		if (isFullProgram(program)) {
			const { modules: moduleResponses, ...resultWithoutModules } = program;
			const moduleIds = program.modules.map(({ id }) => id);
			programs.setWithStatus(program.id, { ...resultWithoutModules, modules: moduleIds }, STATUS.SUCCESS);
			moduleResponses.forEach((mod) => setModuleResponse(mod));
		} else {
			programs.setWithStatus(program.id, { ...program, modules: [] }, STATUS.SUCCESS);
		}
	};

	// Group level
	const fetchGroupProgramsInner = async (groupId: string, isAdmin: boolean) => {
		const { data } = isAdmin
			? await adminApi.adminListProgramsForGroup(groupId)
			: await userApi.listProgramsForGroup(groupId);
		data.forEach((program) => setProgramResponse(program));
		groupPrograms.set(groupId, data.map(({ id }) => id));
	};

	const fetchGroupPrograms = async (groupId: string, isAdmin: boolean, force = false): Promise<void> => {
		if (force || STARTABLE_STATUSES.includes(groupPrograms.getStatus(groupId).value)) {
			const { track } = useAsyncStatus(groupPrograms.getStatus(groupId));
			await track(fetchGroupProgramsInner(groupId, isAdmin));
		}
	};

	const getAllGroupPrograms = (groupId: string): LearningProgram[] => groupPrograms.get(groupId).map(programs.get);
	const getPublishedGroupPrograms = (groupId: string): LearningProgram[] => groupPrograms.get(groupId)
		.map(programs.get)
		.filter(isProgramPublished)
		.map((program) => ({
			...program,
			modules: program.modules.map(modules.get).map(({ id }) => id),
		}));
	const getGroupProgramsStatus = groupPrograms.getStatus;

	const deleteProgram = async (groupId: string, programId: string) => {
		await adminApi.deleteProgramById(programId);
		groupPrograms.set(groupId, groupPrograms.get(groupId).filter((id) => id !== programId));
	};

	// Program level
	const createProgram = async (groupId: string, request: UpsertProgramRequest) => {
		const { data } = await adminApi.upsertProgramForGroup(groupId, request);
		setProgramResponse(data);
		const currentPrograms = groupPrograms.get(groupId);
		groupPrograms.set(groupId, [data.id, ...currentPrograms]);
		return data.id;
	};

	const updateProgram = async (groupId: string, programId: string, payload: UpsertProgramRequest) => {
		programs.update(programId, {
			...payload,
			dateModified: new Date().toISOString(),
			totalCredits: payload.credits || 0,
		});
		const { track } = useAsyncStatus(programs.getStatus(programId));
		await track(adminApi.upsertProgramForGroup(groupId, {
			...payload,
			id: programId,
		}));
	};

	const reorderProgramModules = async (groupId: string, programId: string, orderedModules: string[]): Promise<void> => {
		programs.update(programId, { modules: orderedModules });
		const payload = orderedModules.map((id, orderIndex) => ({ id, orderIndex }));
		const { track } = useAsyncStatus(programs.getStatus(programId));
		await track(adminApi.reorderModules(payload));
	};

	const fetchProgramInner = async (programId: string, isAdmin: boolean): Promise<void> => {
		const { data } = isAdmin
			? await adminApi.adminFetchProgramById(programId)
			: await userApi.fetchProgramById(programId);
		setProgramResponse(data);
	};
	const fetchProgram = async (programId: string, isAdmin: boolean, force = false): Promise<void> => {
		const hasNoModules = programs.get(programId).modules.length === 0;
		if (force || isAdmin || hasNoModules || STARTABLE_STATUSES.includes(programs.getStatus(programId).value)) {
			const { track } = useAsyncStatus(programs.getStatus(programId));
			await track(fetchProgramInner(programId, isAdmin));
		}
	};
	const getProgram = programs.get;
	const getProgramStatus = programs.getStatus;

	// Module level
	const updateModule = async (programId: string, moduleId: string, payload: UpsertModuleRequest): Promise<void> => {
		modules.update(moduleId, { ...payload, dateModified: new Date().toISOString() });
		const programCurrent = programs.get(programId);
		const sendCertificateDateStart = getMaxDateAsStringOrNull(programCurrent.sendCertificateDateStart, payload.publishDateStart);
		programs.update(programId, { sendCertificateDateStart });
		const { track } = useAsyncStatus(modules.getStatus(moduleId));
		await track(adminApi.upsertModuleForProgram(programId, { ...payload, id: moduleId }));
	};

	const reorderModuleParts = async (programId: string, moduleId: string, orderedParts: string[]): Promise<void> => {
		modules.update(moduleId, { parts: orderedParts });
		const payload = orderedParts.map((id, orderIndex) => ({ id, orderIndex }));
		const { track } = useAsyncStatus(programs.getStatus(moduleId));
		await track(adminApi.reorderParts(payload));
	};

	const createModule = async (programId: string, mod: UpsertModuleRequest): Promise<string> => {
		const { track } = useAsyncStatus(programs.getStatus(programId));

		const { data: newValue } = await track(adminApi.upsertModuleForProgram(programId, {
			...mod,
			orderIndex: programs.get(programId).modules.length,
		}));
		setModuleResponse(newValue);

		const programCurrent = programs.get(programId);
		const updatedModules = unique(programCurrent.modules.concat(newValue.id));
		const sendCertificateDateStart = getMaxDateAsStringOrNull(programCurrent.sendCertificateDateStart, mod.publishDateStart);
		programs.update(programId, {
			sendCertificateDateStart,
			modules: updatedModules,
		});

		return newValue.id;
	};

	const deleteModule = async (programId: string, moduleId: string): Promise<void> => {
		const programCurrent = programs.get(programId);
		programs.update(programId, {
			modules: programCurrent.modules.filter((curr) => curr !== moduleId),
		});
		modules.remove(moduleId);
		const { track } = useAsyncStatus(programs.getStatus(programId));
		await track(adminApi.deleteModule(moduleId));
	};

	// Parts
	const deletePart = async (moduleId: string, partId: string): Promise<void> => {
		const moduleCurrent = modules.get(moduleId);
		modules.update(moduleId, {
			parts: moduleCurrent.parts.filter((curr) => curr !== partId),
		});
		parts.remove(partId);

		const { track } = useAsyncStatus(modules.getStatus(moduleId));
		await track(adminApi.deletePart(partId));
	};

	const updatePart = async (moduleId: string, partId: string, payload: UpsertPartRequest): Promise<void> => {
		parts.update(partId, { ...payload, dateModified: new Date().toISOString() });
		const { track } = useAsyncStatus(parts.getStatus(partId));
		await track(adminApi.upsertPartForModule(moduleId, { ...payload, id: partId }));
	};

	const reorderPartSteps = async (moduleId: string, partId: string, orderedSteps: string[]): Promise<void> => {
		parts.update(partId, { steps: orderedSteps });
		const payload = orderedSteps.map((id, orderIndex) => ({ id, orderIndex }));
		const { track } = useAsyncStatus(programs.getStatus(partId));
		await track(adminApi.reorderSteps(payload));
	};

	const createPart = async (moduleId: string, part: UpsertPartRequest): Promise<string> => {
		const { track } = useAsyncStatus(modules.getStatus(moduleId));
		const { data } = await track(adminApi.upsertPartForModule(moduleId, {
			...part,
			orderIndex: modules.get(moduleId).parts.length,
		}));

		setPartResponse(data);

		// Add the new ID to the list of modules
		const moduleCurrent = modules.get(moduleId);
		modules.update(moduleId, {
			parts: unique(moduleCurrent.parts.concat(data.id)),
		});

		return data.id;
	};

	// Steps
	async function deleteStep(partId: string, stepId: string): Promise<void> {
		const partCurrent = parts.get(partId);
		parts.update(partId, {
			steps: partCurrent.steps.filter((curr) => curr !== stepId),
		});
		steps.remove(stepId);

		const { track } = useAsyncStatus(parts.getStatus(partId));
		await track(adminApi.deleteStep(stepId));
	}
	async function updateStep(partId: string, stepId: string, payload: UpsertStepRequest): Promise<void> {
		steps.update(stepId, { ...payload, dateModified: new Date().toISOString() });
		const { track } = useAsyncStatus(steps.getStatus(stepId));
		await track(adminApi.upsertStepForPart(partId, { ...payload, id: stepId }));
	}
	async function createStep(partId: string, step: UpsertStepRequest): Promise<string> {
		const { track } = useAsyncStatus(parts.getStatus(partId));
		const { data } = await track(adminApi.upsertStepForPart(partId, {
			...step,
			orderIndex: parts.get(partId).steps.length,
		}));

		steps.setWithStatus(data.id, data, STATUS.SUCCESS);

		// Add the new ID to the list of steps
		const partCurrent = parts.get(partId);
		parts.update(partId, { steps: unique(partCurrent.steps.concat(data.id)) });

		return data.id;
	}

	async function completeStep(stepId: string, groupId: string): Promise<void> {
		const current = steps.get(stepId);
		if (!current.progress.dateCompleted) {
			steps.update(stepId, { progress: { dateCompleted: new Date().toISOString(), dateStarted: new Date().toISOString() } });
		}
		await userApi.acceptUserStepAction(stepId, groupId, { action: StepAction.Complete });
	}

	return {
		// Groups
		fetchGroupPrograms,
		getPublishedGroupPrograms,
		getAllGroupPrograms,
		getGroupProgramsStatus,
		deleteProgram,
		setProgram: programs.set,
		// Programs
		createProgram,
		updateProgram,
		reorderProgramModules,
		fetchProgram,
		getProgram,
		getProgramStatus,
		// Modules
		createModule,
		updateModule,
		reorderModuleParts,
		deleteModule,
		getModule: modules.get,
		getModuleStatus: modules.getStatus,
		// Parts
		createPart,
		updatePart,
		reorderPartSteps,
		deletePart,
		getPart: parts.get,
		getPartStatus: parts.getStatus,
		// Steps
		createStep,
		deleteStep,
		getStep: steps.get,
		updateStep,
		getStepStatus: steps.getStatus,
		completeStep,
	};
};

export function getEmptyProgram(): LearningProgram {
	return {
		id: '',
		name: '',
		speakers: [],
		credits: 0,
		totalCredits: 0,
		description: '',
		certificateText: '',
		location: {
			street1: '',
			street2: '',
			city: '',
			provStateRegion: EMPTY_REGION,
			country: EMPTY_COUNTRY,
			postalCode: '',
		},
		publishDateStart: null,
		publishDateEnd: null,
		showAccreditedBadge: false,
		sendCertificate: false,
		sendCertificateDateStart: null,
		certificateLocale: CertificateLocale.EnCa,
		dateCreated: new Date().toISOString(),
		dateModified: new Date().toISOString(),
		stepCompletion: {
			total: 0,
			completed: 0,
			lastAction: '',
		},
		modules: [],
	};
}

export function getEmptyModule(): LearningModule {
	return {
		id: '',
		orderIndex: 0,
		name: '',
		speakers: [],
		credits: 0,
		description: '',
		sendCertificate: false,
		sendCertificateDateStart: null,
		publishDateStart: null,
		dateCreated: new Date().toISOString(),
		dateModified: new Date().toISOString(),
		parts: [],
	};
}

export function getEmptyPart(): LearningPart {
	return {
		id: '',
		orderIndex: 0,
		name: '',
		speakers: [],
		credits: 0,
		description: '',
		sendCertificateDateStart: null,
		dateCreated: new Date().toISOString(),
		dateModified: new Date().toISOString(),
		steps: [],
	};
}

export function getEmptyStep(): LearningStep {
	return {
		id: '',
		orderIndex: 0,
		name: '',
		speakers: [],
		dateCreated: new Date().toISOString(),
		dateModified: new Date().toISOString(),
		progress: {
			dateStarted: undefined,
			dateCompleted: undefined,
		},
		content: {
			type: StepContentTypeEnum.Survey,
			id: '',
		},
	};
}

const globalGroupProgramsCache = {};
const globalProgramCache = {};
const globalModuleCache = {};
const globalPartCache = {};
const globalStepCache = {};
export const getLearningProgramsStore = (): LearningProgramStore => useLearningProgramsStore(
	lmsUserApi,
	lmsAdminApi,
	globalGroupProgramsCache,
	globalProgramCache,
	globalModuleCache,
	globalPartCache,
	globalStepCache,
);
